/**
 * Reading a customer's requisition spreadsheet without paying a model to read the whole thing.
 *
 * ---- what we were doing, and what it cost ----
 *
 * `useMailExtraction.ts` turned an attached sheet into TSV — every row, every column — and posted
 * it to DeepSeek. Measured against a real requisition on this bench
 * (`FORM 19-071 BATTERY.xlsx`, 2026-08-12):
 *
 *     sheet 1 as TSV, sent today      4,468 chars   ~1,117 tokens
 *     the rows that actually matter      278 chars      ~70 tokens     (94% less)
 *
 * The other 94% is form boilerplate: instructions, notes, dropdown data, photo placeholders,
 * accounting-code tables. That file's entire order is ONE row — a battery, Pcs, qty 10 — under a
 * header reading `Sr. No. | IMPA No. | Description Of Items | Unit | ROB | Qty Req.`.
 *
 * ---- so there are two savings, and the first is total ----
 *
 * 1. **Read it ourselves.** Those column headings are LABELLED. When they can be found, the items
 *    are read structurally and the model is never called: not a cheaper prompt, no prompt. This is
 *    the same by-label technique `rfqReplyWorkbook.ts` already uses on supplier replies, and for
 *    the same reason — real spreadsheets never put the header in the same cell twice.
 *
 * 2. **When we cannot, send less.** No header found, or found but unreadable? Fall back to the
 *    model with a COMPACTED sheet: the header row and the rows under it, columns that are empty
 *    throughout dropped, and a row cap. Boilerplate above the header and notes below it never
 *    reach the provider.
 *
 * EVERY sheet is read — see `readItemsFromWorkbook`. It used to be only the first, because the
 * whole workbook was being posted to a model and the other five sheets of that file
 * (`PHOTOS`, `data`, `Technical opex`) were 10,000 characters of dropdown values and cost codes
 * that tripled the bill for no signal. Reading structurally costs nothing, so that reason is gone
 * and the limitation went with it.
 */

/** A cell as `read-excel-file` gives it: string, number, Date, or empty. */
export type SheetCell = string | number | boolean | Date | null | undefined
/**
 * A row. ARRAY-LIKE, not necessarily an Array — `read-excel-file` returns its own row objects, and
 * calling `.map` on one throws `header.map is not a function`. Found by running these parsers over
 * all 15 real attachments on the bench (2026-08-12): 14 of them failed on exactly that, while
 * every unit test passed, because the tests hand in plain arrays and real files do not.
 */
export type SheetRow = ArrayLike<SheetCell>

/** Normalises a row to a real array at the boundary, so nothing downstream has to care. */
const cells = (row: SheetRow | null | undefined): SheetCell[] =>
  row == null ? [] : Array.isArray(row) ? (row as SheetCell[]) : Array.from(row)

export interface SheetItem {
  description: string
  qty: number
  uom: string | null
  /** The customer's own code when a column carries one — never invented. */
  item_code: string | null
  /** The customer's line number, echoed exactly (`cust_sno` in the domain). */
  row_no: string | null
  /**
   * The workbook sheet this line came from, when it came from one.
   *
   * Carried on the LINE rather than as a parallel list of groups, so provenance survives every
   * hop to the screen — a purchaser deciding whether two sheets are two requisitions needs to see
   * which line came from where, and a second array beside the lines drifts from them.
   */
  sheet?: string
  /**
   * The ATTACHMENT this line came from, when a mail carried more than one. Two attached
   * requisitions are two documents; keeping the filename on the line is what lets the screen
   * offer one enquiry per file instead of silently pooling them.
   */
  file?: string
}

import { stripFormLabels } from './pdfReadQuality'

const text = (cell: SheetCell): string =>
  cell === null || cell === undefined ? '' : cell instanceof Date ? cell.toISOString().slice(0, 10) : String(cell).trim()

/**
 * A quantity cell as a number — and the unit the customer may have written INTO it. "10 strips"
 * (199E-edited.pdf, ENQ-2026-00289: a form with no unit column) is 10 of `strips`; `Number()`
 * made it NaN and the line vanished. A word alone ("as required") is still no quantity; the
 * cell's word yields to a labelled unit column when there is one.
 */
export function readQuantityCell(raw: string): { qty: number; unit: string | null } {
  const direct = Number(raw)
  if (raw.trim() !== '' && Number.isFinite(direct)) return { qty: direct, unit: null }
  const m = /^\s*(\d+(?:[.,]\d+)?)\s*([A-Za-z][A-Za-z./]{0,11})\.?\s*$/.exec(raw)
  if (!m) return { qty: Number.NaN, unit: null }
  return { qty: Number(m[1].replace(',', '.')), unit: m[2] }
}

/** Header matching is punctuation- and whitespace-blind: `Qty Req.` and `QTY REQ` are one label. */
const normalise = (s: string): string =>
  s
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[.:#()]/g, '')
    .trim()

/**
 * Labels seen on real requisitions, longest-first within each group so `qty req` wins over `qty`.
 * Matching is `startsWith`, because these headings routinely carry a parenthetical — the measured
 * file's is literally `Description Of Items \n(with proper details& Photos in Next sheet)`.
 */
const COLUMNS = {
  description: [
    // Longest first. Every one of these heads a real requisition on this bench.
    'name & description of item',
    'name and description of item',
    'description of items',
    'product description',
    'item description',
    'name of item',
    'description',
    'particulars',
    'item name',
    'item',
  ],
  // `approved qty` / `required qty` head the Shipworkz requisition (SPR-V-0287, 2026-10-27), side by
  // side. Approved comes first: it is what the office signed off to buy, and it is the one we quote.
  qty: ['qty req', 'quantity required', 'qty required', 'approved qty', 'required qty', 'quantity', 'qty', 'req qty'],
  uom: ['unit of measure', 'units', 'unit', 'uom'],
  item_code: ['impa no', 'impa', 'item code', 'part no', 'part number', 'code'],
  // `ml no` is not a guess: it heads a real provision requisition on this bench
  // ("ML NO. | ITEM DESCRIPTIONS | REMARKS | QTY | UNIT"). Labels are only added from observed
  // mail, never invented — an unmatched column costs a null, a WRONGLY matched one costs an order.
  // `sno` is not a typo: `normalise` strips the dot from `S.No`, which is how the MV Kaifan
  // purchase list writes it. Without it every serial on that file — 9A, 9B, 33, 34 — was lost,
  // and `cust_sno` is meant to echo the customer's own numbering exactly.
  // `index`: the Stores Requisition Form (199E, 2026-09-22) heads its serial column so.
  row_no: ['sr no', 'serial no', 'sno', 's no', 'sl no', 'item no', 'line no', 'ml no', 'index'],
} as const

export type ColumnKey = keyof typeof COLUMNS

/**
 * Columns that DESCRIBE the item rather than identify or count it.
 *
 * The MV Kaifan purchase list is why this exists. Its `Item` column says `Genset` on four
 * consecutive rows; what tells them apart is `Purpose` (ER de-watering / cargo / cranes) and
 * `Detailed Specifications` (200 KVA / 600-650 KW / 400kVA / 100kVA). Reading only `Item` produced
 * four identical lines, and a supplier asked to quote those would be quoting blind.
 *
 * A CLOSED list, matched by heading, for the same reason the unit vocabulary is closed: a
 * requisition's later columns are the office's — `LPO#`, `Supplier Name`, `Appr'vd`, `Date` — and
 * folding those into what we send a supplier would be worse than the terse description.
 */
const DETAIL_LABELS = [
  'detailed specification',
  'specification',
  'purpose',
  'description',
  'details',
  'remarks',
  'remark',
  'make',
  'model',
  'brand',
  'size',
  'part',
]

/**
 * Every column whose heading describes the item, excluding the ones already located as a
 * field. A column spent as the code is not also a detail: `Part No` matches the `part` label,
 * and the code came out twice — as `item_code` and joined into the description as
 * "CHIPPING HAMMER 225 GRM — 61 26 11" (2026-10-16), beside our own "(code 61 26 11 not in
 * catalogue)" note.
 */
function detailColumns(header: SheetRow, taken: readonly number[]): number[] {
  const headings = cells(header).map((cell) => normalise(text(cell)))
  const found: number[] = []
  for (let i = 0; i < headings.length; i++) {
    if (taken.includes(i) || headings[i] === '') continue
    // A SECOND code column is still a code. SPR-V-0287 has both `Part Number` and `Item Code`; the
    // latter is spent as `item_code`, and `Part Number` matched `part` and was joined on as
    // "CRC CONTACT CLEANERS 300 ML — 45 09 02" (2026-10-27).
    if (COLUMNS.item_code.some((label) => headings[i] === label || headings[i].startsWith(label))) continue
    if (DETAIL_LABELS.some((label) => headings[i] === label || headings[i].startsWith(label))) found.push(i)
  }
  return found
}

/**
 * Headings a QUANTITY or UNIT column must never match, however well the label fits.
 *
 * `unit` is a prefix of `UNIT PRICE`, and on our own RFQ workbook that mis-read a customer's
 * PRICE as their unit of measure — the audit over all 15 real attachments showed
 * "Printer INK … 4 12", where 12 is money. A wrong unit travels into the RFQ and the quotation
 * without ever looking wrong, so the exclusion is checked before the label.
 */
const NOT_A_MEASURE = /\b(price|rate|amount|cost|value|total)\b/

/**
 * Headings the BARE `item` label must never claim as the description.
 *
 * `item` is a prefix of `Item category`, `Item Group`, `Item sub-section`, `Item Code`, `Item No` —
 * a deck stores requisition (2026-10-28) had no description column, so `Item category` won and
 * every line read "STORE DECK-DECK OTHERS (6100025) — Cam and groove". The customer does not want
 * the category on the line. Only the bare label is guarded: a longer label (`item description`,
 * `item name`) has already said what the column is.
 */
const NOT_A_DESCRIPTION = /\b(category|categories|section|subsection|group|code|no|number|type)\b/

/** Where each column sits on a header row, or `-1`. */
export function locateColumns(header: SheetRow): Record<ColumnKey, number> {
  const headings = cells(header).map((cell) => normalise(text(cell)))
  const found = {} as Record<ColumnKey, number>

  /*
    Labels are tried MOST SPECIFIC FIRST across the whole row, not column-by-column — otherwise a
    bare `Qty` sitting to the left of the real `Qty Req.` wins on position alone. On these forms
    that is the `ROB` hazard in another guise: reading the wrong numeric column produces an order
    that looks entirely plausible and is wrong.
  */
  for (const key of Object.keys(COLUMNS) as ColumnKey[]) {
    found[key] = -1
    const guarded = key === 'uom' || key === 'qty'
    for (const label of COLUMNS[key]) {
      const at = headings.findIndex((h) => {
        if (h === '' || (guarded && NOT_A_MEASURE.test(h))) return false
        // A description heading can offer alternatives — `Part Name / Item Name` — and the label
        // may be any of them, not only the first.
        const options = key === 'description' ? [h, ...h.split('/').map((part) => part.trim())] : [h]
        return options.some(
          (o) => o !== '' && (o === label || o.startsWith(label)) && !(label === 'item' && NOT_A_DESCRIPTION.test(o)),
        )
      })
      if (at >= 0) {
        found[key] = at
        break
      }
    }
  }
  return found
}

/**
 * The row that heads the item table, or `-1`.
 *
 * Scored rather than matched on a single label: a requisition's boilerplate is full of stray words
 * like "Unit" and "Date", and the header is the row where SEVERAL of them line up at once. A
 * description column plus a quantity column is the minimum that can be called an item table.
 */
export function findHeaderRow(rows: readonly SheetRow[]): number {
  for (let i = 0; i < rows.length; i++) {
    const columns = locateColumns(rows[i])
    if (columns.description >= 0 && columns.qty >= 0) return i
  }
  return -1
}

/**
 * Find the item table by SHAPE, when no heading is recognised.
 *
 * The label list only knows templates we have seen, and every new customer brings a new form. But
 * an item table looks the same whatever it is called: a column of DESCRIPTIONS (long-ish text,
 * containing letters) beside a column of QUANTITIES (positive numbers), repeating down consecutive
 * rows. Header text is irrelevant to that.
 *
 * Deliberately conservative — it must find a pattern holding for at least `MIN_ITEM_ROWS`
 * consecutive rows before it will claim anything. A form's address block is one row of text beside
 * one number; an order is not.
 */
const MIN_ITEM_ROWS = 2

interface ShapeGuess {
  header: number
  description: number
  qty: number
  uom: number
}

function looksLikeDescription(value: string): boolean {
  return value.length >= 4 && /[a-z]{3}/i.test(value)
}

function positiveNumber(value: string): boolean {
  const n = Number(value)
  return Number.isFinite(n) && n > 0
}

/**
 * A column that simply counts 1, 2, 3… is a LINE NUMBER, not a quantity.
 *
 * Found the moment shape inference met an unfamiliar form: `Pos | Description | Menge | Einheit`
 * read the position column as the order, because 1/2/3 are perfectly good positive numbers. A
 * serial column ascends by exactly one; a real quantity almost never does across a whole table.
 */
function looksLikeLineNumbers(values: readonly string[]): boolean {
  if (values.length < MIN_ITEM_ROWS) return false
  const numbers = values.map(Number)
  if (numbers.some((n) => !Number.isInteger(n))) return false
  return numbers.every((n, i) => i === 0 || n === numbers[i - 1] + 1)
}

/** A unit is a SHORT word — `Pcs`, `KG`, `CTN` — which is what distinguishes it from a description. */
function looksLikeUnit(value: string): boolean {
  return value.length > 0 && value.length <= 10 && /^[a-z.\/ ]+$/i.test(value)
}

/**
 * The column holding a UNIT for every row of a run, or `-1`.
 *
 * `requireKnown` picks which of its two jobs is being asked for, and they want different bars:
 *
 *   - REPORTING the unit reads whatever the customer wrote. `Stk`, `Einheit`, `bidons` — a run
 *     already established as an order does not get its units second-guessed by our vocabulary.
 *   - CORROBORATING a run of one demands a unit we RECOGNISE, because there the unit is the only
 *     evidence that these cells are an order line at all.
 *
 * An EMPTY run is never corroborated: `[].every()` is vacuously true, and that alone made a form's
 * address block read as an item table.
 */
function unitColumnFor(
  grid: readonly SheetCell[][],
  from: number,
  length: number,
  descriptionAt: number,
  qtyAt: number,
  requireKnown = false,
): number {
  if (length < 1 || from < 0) return -1
  const width = grid.reduce((max, row) => Math.max(max, row.length), 0)
  for (let u = 0; u < width; u++) {
    if (u === descriptionAt || u === qtyAt) continue
    const values = Array.from({ length }, (_, k) => text(grid[from + k][u]))
    const ok = (v: string) => looksLikeUnit(v) && (!requireKnown || KNOWN_UNITS.has(v.toLowerCase().replace(/[. ]/g, '')))
    if (values.every(ok)) return u
  }
  return -1
}

/**
 * Units a label-free read will accept as corroboration. Same closed-vocabulary reasoning as
 * `pdfItems.ts`: a short word is not evidence, a KNOWN unit is.
 */
const KNOWN_UNITS = new Set([
  'pcs', 'pc', 'nos', 'no', 'set', 'sets', 'kg', 'kgs', 'gm', 'g', 'ltr', 'ltrs', 'l', 'mtr', 'mtrs',
  'm', 'box', 'boxes', 'ctn', 'roll', 'rolls', 'pkt', 'pack', 'packs', 'bag', 'bags', 'can', 'cans',
  'tube', 'tubes', 'pair', 'pairs', 'unit', 'units', 'each', 'ea', 'drum', 'drums', 'sheet', 'sheets',
  'bottle', 'bottles', 'month', 'nr', 'pce',
])

export function inferColumnsByShape(rows: readonly SheetRow[]): ShapeGuess | null {
  const grid = rows.map(cells)
  const width = grid.reduce((max, row) => Math.max(max, row.length), 0)
  let best: (ShapeGuess & { runs: number; span: number; unitGap: number; descLen: number }) | null = null
  /** Two readings scored identically and disagreed — see the ranking below. */
  let tied = false

  for (let d = 0; d < width; d++) {
    for (let q = 0; q < width; q++) {
      if (q === d) continue

      // Longest run of consecutive rows where this pairing holds.
      let run = 0
      let runStart = -1
      let bestRun = 0
      let bestStart = -1

      for (let r = 0; r < grid.length; r++) {
        const isItem = looksLikeDescription(text(grid[r][d])) && positiveNumber(text(grid[r][q]))
        if (isItem) {
          if (run === 0) runStart = r
          run++
          if (run > bestRun) {
            bestRun = run
            bestStart = runStart
          }
        } else {
          run = 0
        }
      }

      /*
        A run of ONE is allowed only when a UNIT column corroborates it.

        Measured over the 13 real workbooks: label matching read 13, shape inference alone read 8,
        and every one of the five it missed had exactly ONE item. A single-line requisition is
        common — five of thirteen here — and `MIN_ITEM_ROWS = 2` made it structurally unreadable
        without a recognised heading, which is what turned this into per-file label patching.

        One row of text beside one number really is what a form's address block looks like, so the
        bar is not simply lowered: `Vessel | ALMARJAN | 1` has no unit and stays rejected, while
        `BATTERY 12V | 10 | Pcs` is an order line whatever its columns are called.
      */
      const corroborated = bestRun >= MIN_ITEM_ROWS || unitColumnFor(grid, bestStart, bestRun, d, q, true) >= 0
      if (!corroborated) continue

      // Reject a serial column posing as a quantity — see `looksLikeLineNumbers`.
      const qtyValues = Array.from({ length: bestRun }, (_, k) => text(grid[bestStart + k][q]))
      if (looksLikeLineNumbers(qtyValues)) continue

      /*
        KNOWN LIMITATION, measured rather than guessed. A wide form that puts its metadata band and
        its goods on ONE row gives two readings of the same shape:

            Vessel | ALMARJAN | Ship Staff | ENGINE | AM-E-071 | … | BATTTERY 12V/200A | Pcs | 10
               d=1   d=2                                            d=20                21   23

        `ALMARJAN | 44123` is a description beside a number, so the longest run wins it and the
        requisition reads as an order for 44,123 vessels. Preferring a recognised unit does not
        separate them — `Pcs` is in that row, so BOTH readings can claim it — and neither does
        column adjacency; both were tried and neither moved the measurement, so neither was kept.

        It costs nothing today: the two files that do this both carry headings `findHeaderRow`
        recognises, and label matching runs FIRST. Worth solving when a form appears that has this
        layout AND no heading we know.
      */
      /*
        Longest run first, then the TIGHTEST columns.

        Adjacency is what makes a run of one safe. `Pos | Artikel | Menge | Einheit` offers two
        readings of a single line — `Artikel` against `Menge`, and `Artikel` against the serial in
        `Pos` — and with one row `looksLikeLineNumbers` has nothing to compare, so the serial column
        used to win on position alone and every one-line order read as a quantity of 1. A table's
        description, quantity and unit sit TOGETHER; a serial column is on the far side of the
        description, and a form's metadata is scattered across the sheet.
      */
      const uom = unitColumnFor(grid, bestStart, bestRun, d, q)
      /*
        The UNIT decides it, before anything else does.

        A quantity and its unit are written together — "4 MTR", "10 Pcs" — while an item code sits
        on the far side of the description. `span` alone could not tell them apart: on

            1 | 811676 | [M] BRAKE LINING NON-ASBESTOS… | MTR | 4
                                d=2                        uom=3

        the code at q=1 and the real quantity at q=4 both scored |d-q| + |uom-q| = 3, so the code
        won by being scanned first and the mail read as an order for 811,676 metres (reported
        2026-09-04). Ranking on distance to the UNIT first separates them: 2 against 1.

        Only a tiebreak, never a filter — a table with no unit column at all leaves `unitGap` equal
        for every candidate and `span` decides exactly as it did before.
      */
      const unitGap = uom >= 0 ? Math.abs(uom - q) : 0
      const span = Math.abs(d - q) + unitGap
      /*
        Which column is the DESCRIPTION, when more than one carries text.

        A customer's table routinely has two: the item's own description, and something short and
        repeated beside it — a contractor, a maker, a department. Measured 2026-09-05 on a real
        mail:

            207210 | 1 | 19.002.0012 | WASHING MACHINE MOTOR CARBON INSERTS BRUSHES… | Contractor cezamillb7 | 30. | EA
                                       d=3, 72 chars                                    d=4, 21 chars           q=5

        Both pass `looksLikeDescription`, and `span` preferred d=4 for sitting nearer the quantity
        — so every line read as "Contractor cezamillb7" and was matched, at low confidence, to
        whatever the catalogue had closest to that. The real item never reached the RFQ.

        Length is the signal that separates them: a description describes, a label labels. Averaged
        over the run rather than taken from one row, so a single long cell in an otherwise short
        column does not win it.
      */
      const descLen =
        Array.from({ length: bestRun }, (_, k) => text(grid[bestStart + k][d]).length).reduce(
          (sum, n) => sum + n,
          0,
        ) / Math.max(bestRun, 1)

      /*
        Ranked lexicographically: longest run, then the quantity nearest its unit, then the richest
        description, then the tightest columns. Every one of those is a real signal.

        A DEAD HEAT is not. Two readings level on all four, choosing different columns, is a coin
        flip — and the loops used to break it by arrival order and report the winner with the same
        confidence as an unambiguous read. That is how an item code became a quantity of 811,676
        and a contractor name became the item description. Recorded here and refused below, which
        is exactly what `readItemsDirectly`'s `null` contract asks for: fall back to the model
        rather than guess.
      */
      if (best) {
        const rank =
          bestRun !== best.runs
            ? best.runs - bestRun
            : unitGap !== best.unitGap
              ? unitGap - best.unitGap
              : descLen !== best.descLen
                ? best.descLen - descLen
                : span - best.span

        if (rank > 0) continue
        if (rank === 0) {
          // Same score, different reading — nothing here can choose between them.
          if (d !== best.description || q !== best.qty) tied = true
          continue
        }
      }

      // Strictly better than anything seen, so whatever was tied with the old best is irrelevant.
      tied = false
      best = { header: bestStart - 1, description: d, qty: q, uom, runs: bestRun, span, unitGap, descLen }
    }
  }

  if (!best || tied) return null
  return { header: best.header, description: best.description, qty: best.qty, uom: best.uom }
}

/**
 * The items, read directly — or `null` when this sheet cannot be read confidently.
 *
 * `null` is not a failure to report to the user; it is the signal to fall back to the model. The
 * bar is deliberately high: a wrong line read with false confidence is worse than a model call,
 * because nobody reviews what looks like it worked.
 */
export function readItemsDirectly(rows: readonly SheetRow[]): SheetItem[] | null {
  const headerIndex = findHeaderRow(rows)

  /*
    No heading we recognise. Rather than give up on an unfamiliar template — and every new customer
    brings one — fall back to finding the table by shape. See `inferColumnsByShape`.
  */
  if (headerIndex < 0) {
    const guess = inferColumnsByShape(rows)
    if (guess === null) return null
    /*
      The shape guess only FILLS IN what the headings do not say. When the row it settled on is a
      header with recognisable labels — just not a quantity label we know — a column the customer
      labelled wins over the guess. SPR-V-0287 (2026-10-27) is why: its `Required Qty` heading was
      unknown, the guess took `Item Section/ Equipment` for the description and `Status` for the
      unit, and every line went out as "GALLEY & CABIN (STORES) — 174272", unit "Approved".
    */
    const labelled = locateColumns(rows[guess.header] ?? [])
    const pick = (key: ColumnKey, guessed: number) => (labelled[key] >= 0 ? labelled[key] : guessed)
    return readRows(rows, guess.header, {
      description: pick('description', guess.description),
      qty: pick('qty', guess.qty),
      uom: pick('uom', guess.uom),
      item_code: labelled.item_code,
      row_no: labelled.row_no,
    })
  }

  return readRows(rows, headerIndex, reconcileWithData(rows, headerIndex, locateColumns(rows[headerIndex])))
}

/**
 * The heading's word, checked against the cells under it.
 *
 * `PROVISION.xlsx` (mail e0ostsarak, 2026-09-12) is headed `… | UNIT | QTY | …` and every row
 * under it has the NUMBER in the UNIT column and `PKT` in the QTY column — the author swapped
 * them, and nobody reading the sheet would notice. Trusting the heading found "PKT" where a
 * quantity was expected and rejected the whole table.
 *
 * So when the labelled quantity column holds NO positive number in the data, and the labelled
 * unit column holds one on most rows, the two are swapped. Nothing else is touched, and a
 * heading with even one real quantity under it is left exactly as read — a single odd row is
 * the row's problem, not the heading's.
 */
function reconcileWithData(
  rows: readonly SheetRow[],
  headerIndex: number,
  columns: Record<ColumnKey, number>,
): Record<ColumnKey, number> {
  if (columns.qty < 0 || columns.uom < 0) return columns
  const data = rows.slice(headerIndex + 1).map(cells).filter((row) => text(row[columns.description]) !== '')
  if (data.length === 0) return columns
  const numbersUnder = (col: number) => data.filter((row) => positiveNumber(text(row[col]))).length
  const unitsUnder = (col: number) => data.filter((row) => looksLikeUnit(text(row[col]))).length
  if (numbersUnder(columns.qty) > 0) return columns
  if (numbersUnder(columns.uom) * 2 < data.length) return columns
  if (unitsUnder(columns.qty) === 0) return columns
  return { ...columns, qty: columns.uom, uom: columns.qty }
}

/** Reads the rows under `headerIndex` using already-located columns. */
function readRows(
  rows: readonly SheetRow[],
  headerIndex: number,
  columns: Record<ColumnKey, number>,
): SheetItem[] | null {
  const items: SheetItem[] = []
  const withoutQty: { at: number; description: string }[] = []
  const details = headerIndex >= 0 ? detailColumns(rows[headerIndex], Object.values(columns)) : []
  let lastRowWithQty = -1

  for (let i = headerIndex + 1; i < rows.length; i++) {
    const row = cells(rows[i])
    /*
      The item, plus whatever describes it. Four rows reading `Genset` are indistinguishable to
      the purchaser choosing suppliers and to the supplier being asked to quote; `Genset — cargo
      operation — 600-650 KW, 440V, 60 Hz` is an order line.
    */
    // `stripFormLabels`: a PO form's per-item sub-labels (`Position No. : …`) ride along in
    // the description cell when DI reads the page (2026-09-21); they are the form, not the item.
    const parts = [stripFormLabels(text(row[columns.description])), ...details.map((c) => text(row[c]))].filter((p) => p !== '')
    const description = parts.join(' — ')
    const { qty, unit: unitInQty } = readQuantityCell(text(row[columns.qty]))

    // A row with NEITHER is not an item: a blank spacer, a total, or the notes block every one of
    // these forms ends with. Skipped silently, as it should be.
    if (description === '') continue

    /*
      A row that names something but carries no quantity is different, and must NOT be silent. The
      MV Kaifan list has one — `10 · Diesel · For powering gensets · (no QTY) · 1 m3 ISO Tanks x 6
      nos.` — a real requirement whose quantity lives in the specification instead of the column.
      Dropping it without a word means seven rows go in and six come out, and nothing on screen
      says which one is missing.
    */
    const noQty = !Number.isFinite(qty) || qty <= 0
    // A row with no quantity AND nothing in the description cell itself is a form's sub-row
    // (`Remarks To Vendor :` under every PO line, carried in from a detail column) — not a line.
    // A cell ending in a colon is a label too (`Vendor :`, when DI splits the sub-row's words).
    const ownDescription = stripFormLabels(text(row[columns.description]))
    if (noQty && (ownDescription === '' || /:\s*$/.test(ownDescription))) continue
    if (noQty) {
      // Position recorded — whether this is a real requirement or the form's own footer depends
      // on what comes AFTER it. See the filter below, which also decides whether it stays a line.
      withoutQty.push({ at: i, description })
    } else {
      lastRowWithQty = i
    }
    items.push({
      description,
      // Akhil, 2026-09-22: "no dropping of items". A line whose quantity could not be read is
      // still the customer's line — it goes through at 0, for the purchaser to complete.
      qty: noQty ? 0 : qty,
      uom: (columns.uom >= 0 ? text(row[columns.uom]) || null : null) ?? unitInQty,
      item_code: columns.item_code >= 0 ? text(row[columns.item_code]) || null : null,
      row_no: columns.row_no >= 0 ? text(row[columns.row_no]) || null : null,
      ...(noQty ? { at: i } : {}),
    } as SheetItem & { at?: number })
  }

  /*
    Only rows INSIDE the item block are worth reporting.

    `FORM 19` ends with an instruction table — `ENGINE | Engineering Stores`, `DECK | Deck
    Stores` — which has a description and no quantity, exactly like a real requirement missing its
    figure. Reporting those buried the one that mattered under six that did not: the message read
    "check Engineering Stores, Deck Stores, All Routine service Related Activities…" and a
    purchaser learns to ignore it.

    A row before the LAST one carrying a quantity is inside the table. After it is the footer.
    Diesel sits between two priced rows on the MV Kaifan list and survives; the instruction block
    sits below everything and does not.
  */
  const lastItemAt = lastRowWithQty
  lastSkippedForQty = withoutQty.filter((row) => row.at < lastItemAt).map((row) => row.description)
  // The footer rows (after the last quantity) are not lines; a no-quantity row inside the block is.
  const kept = items
    .filter((item) => (item as { at?: number }).at === undefined || ((item as { at?: number }).at as number) < lastItemAt)
    .map(({ at: _at, ...item }: SheetItem & { at?: number }) => item)
  return kept.length > 0 ? kept : null
}

/**
 * Descriptions from the last `readRows` that named something but carried no quantity.
 *
 * A module-level handoff rather than a changed return type, because `readItemsDirectly`'s `null`
 * vs `SheetItem[]` contract is depended on in six places and widening it to carry a second value
 * would touch all of them for one report. Read it immediately after the call that produced it —
 * `readItemsFromWorkbook` does, per sheet.
 */
let lastSkippedForQty: string[] = []

/** Past this the sheet is a catalogue, not an order, and the model gets a truncated view. */
export const MAX_MODEL_ROWS = 200

export interface CompactedSheet {
  /** TSV: the header row, then the data rows, with dead columns removed. */
  text: string
  /** Rows dropped by the cap. Non-zero must be SHOWN — a silent truncation reads as a short order. */
  truncated: number
}

/**
 * The sheet reduced to what a model needs to read it.
 *
 * Everything above the header row goes (vessel, dates, instructions), everything blank goes, and
 * any column empty across every kept row goes with it — the measured file has 22 columns of which
 * 15 carry nothing at all.
 *
 * With no header found, the rows are still stripped of blanks and capped rather than passed
 * through whole: we do not know where the items are, but we do know that empty cells cost tokens
 * and carry nothing.
 */
export function compactForModel(rows: readonly SheetRow[]): CompactedSheet {
  const headerIndex = findHeaderRow(rows)
  const from = headerIndex >= 0 ? headerIndex : 0

  const kept = rows.slice(from).map(cells).filter((row) => row.some((cell) => text(cell) !== ''))
  const capped = kept.slice(0, MAX_MODEL_ROWS)

  // A column is dead when nothing in ANY kept row fills it.
  const width = capped.reduce((max, row) => Math.max(max, row.length), 0)
  const live: number[] = []
  for (let c = 0; c < width; c++) {
    if (capped.some((row) => text(row[c]) !== '')) live.push(c)
  }

  const text_ = capped
    .map((row) => live.map((c) => text(row[c]).replace(/\s+/g, ' ')).join('\t'))
    .join('\n')

  return { text: text_, truncated: Math.max(0, kept.length - capped.length) }
}

/* ==============================================================================================
 * A workbook, not a sheet
 * ============================================================================================ */

export interface WorkbookSheet {
  name: string
  rows: SheetRow[]
}

/** One sheet's worth of order, kept apart from the others. */
export interface SheetGroup {
  sheet: string
  items: SheetItem[]
}

export interface WorkbookItems {
  /** Every line, in workbook order — for callers that want one enquiry from the whole file. */
  items: SheetItem[]
  /**
   * The same lines, still grouped BY SHEET.
   *
   * A workbook's sheets are often separate requisitions rather than one order split for space —
   * deck on one, engine on the next, going to different suppliers under different enquiry types.
   * Flattening them is a decision the reader has no business making on its own, so both shapes are
   * returned and the caller chooses.
   */
  groups: SheetGroup[]
  /** Sheet names that contributed a line — shown, so "which sheet did this come from" has an answer. */
  from: string[]
  /**
   * Lines `items` dropped as an exact restatement of one from an earlier sheet. The GROUPS keep
   * them — see `readItemsFromWorkbook`.
   */
  duplicates: number
  /** Rows that named something but carried no quantity. Reported, never silently dropped. */
  skippedForQty: string[]
}

/** Two lines are the same order line when the customer wrote the same thing. */
const lineKey = (item: SheetItem): string =>
  `${item.description.trim().toLowerCase()}|${item.qty}|${(item.uom ?? '').trim().toLowerCase()}`

/**
 * Read items from EVERY sheet, in order, keeping the sheets that carry an order.
 *
 * ---- why not just the first ----
 *
 * A requisition routinely runs onto a second sheet — deck on one, engine on the next — and reading
 * only the first silently halves the order. Nothing about that is visible on screen: the lines
 * that made it look exactly like the lines that did not.
 *
 * ---- and why not simply concatenate ----
 *
 * Because these workbooks restate themselves. `FORM 19-071 BATTERY.xlsx` on this bench carries its
 * one battery on the `FORM 19` sheet AND again on `PURCHASE DEPT use`, under different column
 * headings — a summary for the office, not a second battery. Concatenating orders two.
 *
 * So an EXACT restatement — same description, same quantity, same unit — is dropped, and the count
 * is reported rather than swallowed. Two sheets genuinely ordering the same thing twice is the
 * case this gets wrong; it is rarer than the restatement, and the purchaser reviews these lines.
 */
export function readItemsFromWorkbook(sheets: readonly WorkbookSheet[]): WorkbookItems {
  const items: SheetItem[] = []
  const groups: SheetGroup[] = []
  const seen = new Set<string>()
  const from: string[] = []
  const skippedForQty: string[] = []
  let duplicates = 0

  for (const sheet of sheets) {
    const read = readItemsDirectly(sheet.rows)
    if (!read) continue

    const stamped = read.map((item) => ({ ...item, sheet: sheet.name }))
    skippedForQty.push(...lastSkippedForQty)

    /*
      The GROUP keeps every line this sheet had. The flattened list does not.

      Deduping at read time defeated the thing this is for: `MV Kaifan … .xlsx` has a `Source` and
      a `Procure` sheet, and if they share lines the second sheet's group vanished — leaving no
      second requisition to create an enquiry from. Which is precisely what the purchaser asked to
      be offered.

      So restatement is collapsed only where it would actually double an order: in `items`, the
      list used when the purchaser chooses ONE combined enquiry. Split, each sheet is itself.
    */
    groups.push({ sheet: sheet.name, items: stamped })
    from.push(sheet.name)

    /*
      Checked against EARLIER sheets only, never against this one.

      The 129-line provision order has two identical rows in a single sheet — the customer asked
      for both, and collapsing them silently dropped one. Restatement is a property of a SECOND
      sheet summarising the first, so this sheet's own keys join `seen` only after it is done.
    */
    const keysThisSheet: string[] = []
    for (const item of stamped) {
      const key = lineKey(item)
      keysThisSheet.push(key)
      if (seen.has(key)) {
        duplicates++
        continue
      }
      items.push(item)
    }
    for (const key of keysThisSheet) seen.add(key)
  }

  return { items, groups, from, duplicates, skippedForQty }
}

